<app-admin-page-shell
  titleKey="app.admin.users.title"
  subtitleKey="app.admin.users.subtitle"
  [panelled]="false"
>
  <div class="users-admin">
    <section class="users-admin__stats" aria-label="User overview statistics">
      <app-admin-stat-card
        accent="primary"
        labelKey="app.admin.users.stats.totalUsers"
        [value]="stats()?.totalUsers ?? null"
        [loading]="statsLoading()"
      />
      <app-admin-stat-card
        accent="success"
        labelKey="app.admin.users.stats.activeUsers"
        [value]="stats()?.activeUsers ?? null"
        [loading]="statsLoading()"
      />
      <app-admin-stat-card
        accent="purple"
        labelKey="app.admin.users.stats.inProgress"
        [value]="stats()?.inProgressCount ?? null"
        [loading]="statsLoading()"
        hintKey="app.admin.users.stats.inProgressHint"
      />
      <app-admin-stat-card
        accent="warning"
        labelKey="app.admin.users.stats.registeredLast7Days"
        [value]="stats()?.registeredLast7Days ?? null"
        [loading]="statsLoading()"
      />
    </section>

    @if (statsError()) {
      <div class="users-admin__error">
        <p>{{ 'app.admin.users.errorStats' | translate }}</p>
        <app-cta-button variant="light" size="small" (clicked)="refresh()">
          <span cta-label>{{ 'app.admin.users.retry' | translate }}</span>
        </app-cta-button>
      </div>
    } @else {
      @if (stats(); as overview) {
        <section class="users-admin__funnel" aria-label="Onboarding funnel">
          <div class="funnel__header">
            <h2 class="funnel__title">
              {{ 'app.admin.users.funnel.title' | translate }}
            </h2>
            <p class="funnel__hint">
              {{ 'app.admin.users.funnel.hint' | translate }}
            </p>
          </div>

          <ul class="funnel__steps">
            @for (step of funnelSteps; track step.status) {
              <li class="funnel-step">
                <button
                  type="button"
                  class="funnel-step__button"
                  (click)="filterByOnboardingStatus(step.status)"
                >
                  <div class="funnel-step__label-row">
                    <app-onboarding-status-badge [status]="step.status" />
                    <span class="funnel-step__count">{{ funnelCount(step.key) }}</span>
                  </div>
                  <div class="funnel-step__bar" aria-hidden="true">
                    <span
                      class="funnel-step__fill funnel-step__fill--{{ step.accent }}"
                      [style.width.%]="funnelWidth(step.key)"
                    ></span>
                  </div>
                </button>
              </li>
            }
          </ul>

          <dl class="funnel__meta">
            <div>
              <dt>{{ 'app.admin.users.stats.finalized' | translate }}</dt>
              <dd>{{ overview.statusFinalized }}</dd>
            </div>
            <div>
              <dt>{{ 'app.admin.users.stats.verifiedUsers' | translate }}</dt>
              <dd>{{ overview.verifiedUsers }}</dd>
            </div>
            <div>
              <dt>{{ 'app.admin.users.stats.parentCreated' | translate }}</dt>
              <dd>{{ overview.createdByParentCount }}</dd>
            </div>
            <div>
              <dt>{{ 'app.admin.users.stats.registeredLast30Days' | translate }}</dt>
              <dd>{{ overview.registeredLast30Days }}</dd>
            </div>
          </dl>
        </section>
      }
    }

    <section class="users-admin__directory" aria-label="User directory">
      <div class="directory__header">
        <div>
          <h2 class="directory__title">
            {{ 'app.admin.users.directory.title' | translate }}
          </h2>
          <p class="directory__hint">
            {{ 'app.admin.users.directory.hint' | translate }}
          </p>
        </div>
        <app-cta-button variant="light" size="small" (clicked)="refresh()">
          <span cta-label>{{ 'app.admin.users.refresh' | translate }}</span>
        </app-cta-button>
      </div>

      <form class="directory__filters" (ngSubmit)="applyFilters()">
        <label class="filter-field">
          <span>{{ 'app.admin.users.filters.search' | translate }}</span>
          <input
            type="search"
            [(ngModel)]="filterSearch"
            name="search"
            [placeholder]="'app.admin.users.filters.searchPlaceholder' | translate"
          />
        </label>

        <label class="filter-field">
          <span>{{ 'app.admin.users.filters.role' | translate }}</span>
          <select [(ngModel)]="filterRole" name="role">
            <option value="">
              {{ 'app.admin.users.filters.allRoles' | translate }}
            </option>
            @for (role of roleOptions.slice(1); track role) {
              <option [value]="role">
                {{ 'app.admin.users.roles.' + role | translate }}
              </option>
            }
          </select>
        </label>

        <label class="filter-field">
          <span>{{ 'app.admin.users.filters.onboarding' | translate }}</span>
          <select [(ngModel)]="filterOnboarding" name="onboarding">
            <option value="">
              {{ 'app.admin.users.filters.allOnboarding' | translate }}
            </option>
            @for (status of onboardingOptions.slice(1); track status) {
              <option [value]="status">
                {{ 'app.admin.users.onboardingStatus.' + status | translate }}
              </option>
            }
          </select>
        </label>

        <label class="filter-field">
          <span>{{ 'app.admin.users.filters.active' | translate }}</span>
          <select [(ngModel)]="filterActive" name="active">
            <option value="all">
              {{ 'app.admin.users.filters.allActive' | translate }}
            </option>
            <option value="active">
              {{ 'app.admin.users.active' | translate }}
            </option>
            <option value="inactive">
              {{ 'app.admin.users.inactive' | translate }}
            </option>
          </select>
        </label>

        <div class="filter-actions">
          <app-cta-button variant="light" size="small" type="submit">
            <span cta-label>{{ 'app.admin.users.filters.apply' | translate }}</span>
          </app-cta-button>
          <button type="button" class="filter-reset" (click)="resetFilters()">
            {{ 'app.admin.users.filters.reset' | translate }}
          </button>
        </div>
      </form>

      <div class="directory__panel">
        @if (usersLoading()) {
          <div class="directory__loading">
            <mat-spinner diameter="40"></mat-spinner>
            <p>{{ 'app.admin.users.loading' | translate }}</p>
          </div>
        } @else if (usersError()) {
          <div class="users-admin__error">
            <p>{{ 'app.admin.users.errorList' | translate }}</p>
            <app-cta-button variant="light" size="small" (clicked)="refresh()">
              <span cta-label>{{ 'app.admin.users.retry' | translate }}</span>
            </app-cta-button>
          </div>
        } @else if (hasUsers()) {
          <app-users-table [users]="users()" (viewUser)="openUserDetail($event)" />

          <div class="user-cards user-cards--mobile">
            @for (user of users(); track user.id) {
              <article class="user-card">
                <div class="user-card__header">
                  <div>
                    <h3>{{ user.displayName || user.userName }}</h3>
                    <p>{{ '@' + user.userName }}</p>
                  </div>
                  <app-onboarding-status-badge [status]="user.onboardingStatus" />
                </div>
                <dl class="user-card__meta">
                  <div>
                    <dt>{{ 'app.admin.users.table.role' | translate }}</dt>
                    <dd>{{ 'app.admin.users.roles.' + user.role | translate }}</dd>
                  </div>
                  <div>
                    <dt>{{ 'app.admin.users.table.progress' | translate }}</dt>
                    <dd>{{ user.onboardingProgressPercent }}%</dd>
                  </div>
                  <div>
                    <dt>{{ 'app.admin.users.table.registered' | translate }}</dt>
                    <dd>{{ user.creationDate * 1000 | date: 'mediumDate' }}</dd>
                  </div>
                </dl>
                <button type="button" class="user-card__action" (click)="openUserDetail(user)">
                  {{ 'app.admin.users.viewDetails' | translate }}
                </button>
              </article>
            }
          </div>

          @if (totalCount() > pageSize()) {
            <app-daily-facts-pagination
              [page]="page()"
              [pageSize]="pageSize()"
              [totalCount]="totalCount()"
              [isLoading]="usersLoading()"
              (pageChange)="goToPage($event)"
            />
          }
        } @else {
          <p class="directory__empty">
            {{ 'app.admin.users.empty' | translate }}
          </p>
        }
      </div>
    </section>
  </div>

  <app-modal
    [isOpen]="selectedUser() !== null"
    [config]="{
      title: 'app.admin.users.detail.title',
      minWidth: 'min(92vw, 42rem)',
      maxWidth: 'min(92vw, 42rem)',
      minHeight: 'auto',
      padding: '1.25rem 1.5rem 1.5rem'
    }"
    (closed)="closeUserDetail()"
  >
    @if (detailLoading()) {
      <div class="detail-loading">
        <mat-spinner diameter="36"></mat-spinner>
        <p>{{ 'app.admin.users.detail.loading' | translate }}</p>
      </div>
    } @else if (detailError()) {
      <p class="detail-error">{{ 'app.admin.users.detail.error' | translate }}</p>
    } @else {
      @if (userDetail(); as user) {
        <div class="user-detail">
          <div class="user-detail__hero">
            <div>
              <h3>{{ user.summary.displayName || user.summary.userName }}</h3>
              <p class="user-detail__username">{{ '@' + user.summary.userName }}</p>
              @if (user.summary.emailMasked) {
                <p class="user-detail__email">{{ user.summary.emailMasked }}</p>
              }
            </div>
            <app-onboarding-status-badge [status]="user.summary.onboardingStatus" />
          </div>

          <div class="user-detail__progress">
            <div class="user-detail__progress-bar" aria-hidden="true">
              <span
                [style.width.%]="user.summary.onboardingProgressPercent"
              ></span>
            </div>
            <p>{{ detailProgressLabel(user) }}</p>
          </div>

          <dl class="user-detail__grid">
            <div>
              <dt>{{ 'app.admin.users.detail.role' | translate }}</dt>
              <dd>{{ 'app.admin.users.roles.' + user.summary.role | translate }}</dd>
            </div>
            <div>
              <dt>{{ 'app.admin.users.detail.accountType' | translate }}</dt>
              <dd>{{ user.summary.accountType }}</dd>
            </div>
            <div>
              <dt>{{ 'app.admin.users.detail.registered' | translate }}</dt>
              <dd>{{ user.summary.creationDate * 1000 | date: 'medium' }}</dd>
            </div>
            <div>
              <dt>{{ 'app.admin.users.detail.active' | translate }}</dt>
              <dd>
                {{
                  (user.summary.isActive
                    ? 'app.admin.users.active'
                    : 'app.admin.users.inactive'
                  ) | translate
                }}
              </dd>
            </div>
            <div>
              <dt>{{ 'app.admin.users.detail.verified' | translate }}</dt>
              <dd>
                {{
                  (user.summary.verified
                    ? 'app.admin.users.yes'
                    : 'app.admin.users.no'
                  ) | translate
                }}
              </dd>
            </div>
            <div>
              <dt>{{ 'app.admin.users.detail.parentCreated' | translate }}</dt>
              <dd>
                {{
                  (user.summary.createdByParent
                    ? 'app.admin.users.yes'
                    : 'app.admin.users.no'
                  ) | translate
                }}
              </dd>
            </div>
            @if (user.birthDate) {
              <div>
                <dt>{{ 'app.admin.users.detail.birthDate' | translate }}</dt>
                <dd>{{ user.birthDate }}</dd>
              </div>
            }
            @if (user.country) {
              <div>
                <dt>{{ 'app.admin.users.detail.country' | translate }}</dt>
                <dd>{{ user.country }}</dd>
              </div>
            }
            @if (user.phoneMasked) {
              <div>
                <dt>{{ 'app.admin.users.detail.phone' | translate }}</dt>
                <dd>{{ user.phoneMasked }}</dd>
              </div>
            }
            <div>
              <dt>{{ 'app.admin.users.detail.personalData' | translate }}</dt>
              <dd>
                {{
                  (user.hasPersonalData
                    ? 'app.admin.users.yes'
                    : 'app.admin.users.no'
                  ) | translate
                }}
              </dd>
            </div>
            <div>
              <dt>{{ 'app.admin.users.detail.avatar' | translate }}</dt>
              <dd>
                {{
                  (user.hasAvatar
                    ? 'app.admin.users.yes'
                    : 'app.admin.users.no'
                  ) | translate
                }}
              </dd>
            </div>
            <div>
              <dt>{{ 'app.admin.users.detail.linkedParents' | translate }}</dt>
              <dd>{{ user.linkedParentsCount }}</dd>
            </div>
            <div>
              <dt>{{ 'app.admin.users.detail.linkedChildren' | translate }}</dt>
              <dd>{{ user.linkedChildrenCount }}</dd>
            </div>
            @if (user.familyLinkStatus) {
              <div>
                <dt>{{ 'app.admin.users.detail.familyLink' | translate }}</dt>
                <dd>{{ user.familyLinkStatus }}</dd>
              </div>
            }
            <div>
              <dt>{{ 'app.admin.users.detail.gdpr' | translate }}</dt>
              <dd>
                {{
                  (user.gdprAccepted
                    ? 'app.admin.users.yes'
                    : 'app.admin.users.no'
                  ) | translate
                }}
              </dd>
            </div>
            <div>
              <dt>{{ 'app.admin.users.detail.marketing' | translate }}</dt>
              <dd>
                {{
                  (user.marketingAccepted
                    ? 'app.admin.users.yes'
                    : 'app.admin.users.no'
                  ) | translate
                }}
              </dd>
            </div>
          </dl>
        </div>
      }
    }
  </app-modal>
</app-admin-page-shell>
